import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMutation,
  useQueryClient
} from '@tanstack/react-query';

import {
  Modal,
  Input,
  SkeletonLoader,
  StatusIndicator
} from '@unified-checkout/design-system';
import { Check, Xmark } from '@unified-checkout/design-system/icons/index';

import type { ISavedBillsInquiryModal } from '@app/dashboard/types/innerLayout/bills/saved-bills-inquiry-modal';

import { SummaryBox } from '@app/dashboard/components/inner_layout/Cards/SummaryBox';
import { MultiSectionCard } from '@app/dashboard/components/inner_layout/MultiSectionCard';
import { BalancesDetails } from '@app/dashboard/components/inner_layout/BalanceDetails';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { confirmPayment } from '@app/dashboard/services/inner_layout/bills/confirm-payment';
import { confirmSmsSteps } from '@app/dashboard/services/inner_layout/bills/confirm-sms-steps';

import styles from './inquiry-saved-bills.module.scss';

const STEP = {
  DETAILS: 1,
  PARTIAL: 2,
} as const;

export const SavedBillsInquiryModal = ({
  showInquiryModal,
  setShowInquiryModal,
  selectedBill,
  billsData,
  data,
  isLoading
}: ISavedBillsInquiryModal) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<number>(STEP.DETAILS);
  const [partialAmount, setPartialAmount] = useState('');
  const [result, setResult] = useState<any>(null);

  const payPartial = data?.data?.data?.payment_details?.allow_partial;
  const inquiryData = data?.data?.data;

  const { mutate: handleConfirmPayment, isPending: isSubmitting } = useMutation({
    mutationFn: confirmPayment,
    onSuccess: (responseData: any) => {
      if (responseData?.data?.instructions) {
        setResult({
          status: 'sms',
          title: t('innerLayout.billsPage.addFundsModal.smsStepsTitle'),
          instructions: responseData.data.instructions,
          statusCheck: responseData.data.status_check,
        });
        return;
      }

      setResult({
        status: 'success',
        title: responseData?.data?.title || t('innerLayout.billsPage.allServices.paymentSuccess'),
        description: responseData?.data?.description || t('innerLayout.billsPage.allServices.paymentSuccessHint'),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MAIN_DATA] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SAVED_BILLS] });
    },
    onError: (error: any) => {
      const errData = error?.response?.data;
      setResult({
        status: 'error',
        title: errData?.title || errData?.message || t('errors.somethingWentWrong'),
        description: errData?.description,
      });
    },
  });

  const { mutate: handleConfirmSms, isPending: isConfirmingSms } = useMutation({
    mutationFn: confirmSmsSteps,
    onSuccess: (responseData: any) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MAIN_DATA] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SAVED_BILLS] });
      setResult({
        status: 'success',
        title: responseData?.data?.title || t('innerLayout.billsPage.allServices.paymentSuccess'),
        description: responseData?.data?.description || t('innerLayout.billsPage.allServices.paymentSuccessHint'),
      });
    },
    onError: (error: any) => {
      const errData = error?.response?.data;
      setResult({
        status: 'error',
        title: errData?.message || t('errors.somethingWentWrong'),
        description: errData?.description,
      });
    },
  });

  const handleClose = () => {
    setShowInquiryModal(false);
    setStep(STEP.DETAILS);
    setPartialAmount('');
    setResult(null);
  };

  const handlePayNow = () => {
    if (result?.instructions) {
      handleConfirmSms(result.statusCheck);
      return;
    }

    const requiredData = inquiryData?.bill_details || {};

    handleConfirmPayment({
      category_id: requiredData?.category_id,
      service_id: requiredData?.service_id,
      required_data: requiredData.required_data,
      fees: requiredData?.fees,
      amount: step === STEP.PARTIAL ? partialAmount : requiredData?.amount,
      bill_id: requiredData?.bill_id,
      save_bill: false,
    });
  };

  const handlePayPartially = () => {
    setStep(STEP.PARTIAL);
  };

  const renderHeader = () => {
    if (step === STEP.PARTIAL) {
      return (
        <div className={styles['inquiry-saved-bills__header']}>
          <span className={styles['inquiry-saved-bills__header__title']}>
            {t('innerLayout.billsPage.allServices.enterNewValue')}
          </span>
          <span className={styles['inquiry-saved-bills__header__description']}>
            {t('innerLayout.billsPage.allServices.enterAmountToPay')}
          </span>
        </div>
      );
    }

    return (
      <div className={styles['inquiry-saved-bills__header']}>
        {selectedBill?.category_icon && (
          <img
            className={styles['inquiry-saved-bills__header__icon']}
            src={selectedBill?.category_icon}
            alt={selectedBill?.name}
          />
        )}
        <span className={styles['inquiry-saved-bills__header__title']}>
          {selectedBill?.name}
        </span>
      </div>
    );
  };

  const renderCashback = () => {
    return (
      inquiryData?.payment_details?.cashback_data?.title && (
        <div className={styles['inquiry-saved-bills__body__note']}>
          <div
            className={styles['inquiry-saved-bills__body__note__title']}
            style={{ color: inquiryData.payment_details.cashback_data.title_color }}
          >
            <img src={inquiryData.payment_details.cashback_data.icon} alt="icon" />
            {inquiryData.payment_details.cashback_data.title}
          </div>
          <div className={styles['inquiry-saved-bills__body__note__description']}>
            {inquiryData.payment_details.cashback_data.sub_title}
          </div>
        </div>
      )
    )
  }

  const renderPartialPayment = () => {
    return (
      <div className={styles['inquiry-saved-bills__body__partial-payment']}>
        <Input
          name='partial-payment'
          type='number'
          label={t('innerLayout.billsPage.allServices.partialPaymentInputLabel')}
          placeholder={"0.00"}
          value={partialAmount}
          error={Number(partialAmount) > Number(inquiryData?.bill_details?.amount) ? t('innerLayout.billsPage.allServices.partialPaymentError') : ""}
          onChange={(e) => setPartialAmount(e.target.value)}
        />
        <div className={styles['inquiry-saved-bills__body__partial-payment__balance']}>
          <div className={styles['inquiry-saved-bills__body__partial-payment__balance__label']}>
            {t('innerLayout.billsPage.allServices.totalBillAmount')}
          </div>
          <div className={styles['inquiry-saved-bills__body__partial-payment__balance__value']}>
            {inquiryData?.bill_details?.amount} {inquiryData?.payment_details?.balance?.currency}
          </div>
        </div>
      </div>
    );
  }
  const renderBalance = () => {
    return (
      <BalancesDetails
        title={t('innerLayout.billsPage.billsBalance')}
        amount={billsData?.data?.data?.balance?.amount}
        currency={billsData?.data?.data?.balance?.currency}
      />
    );
  }

  const renderDetailsStep = () => {
    if (isLoading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' width='30%' />
          <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' width='100%' />
          <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' width='30%' />
          <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' width='100%' />
        </div>
      );
    }

    const detailPairs = (inquiryData?.payment_details?.details ?? []).map((item: any) => ({
      label: item.title,
      value: item.value,
    }));

    return (
      <>
        {!!detailPairs.length && (
          <SummaryBox
            title={t('innerLayout.billsPage.allServices.detailsTitle')}
            itemsList={detailPairs}
            width="100%"
          />
        )}
        {renderCashback()}
      </>
    );
  };

  const getBtnsProps = () => {
    const isWorking = isSubmitting || isConfirmingSms;
    const hasInstructions = !!result?.instructions;
    const partialStep = step === STEP.PARTIAL;

    if (result && !hasInstructions) return undefined;

    const buttons = [
      {
        show: partialStep,
        text: t('form.confirm'),
        onClickHandler: handlePayNow,
        disabled: isWorking || !partialAmount || Number(partialAmount) > Number(inquiryData?.bill_details?.amount),
        loading: isWorking,
      },
      {
        show: partialStep,
        color: 'secondary' as const,
        variant: 'outlined' as const,
        text: t('form.back'),
        onClickHandler: () => setStep(STEP.DETAILS),
        disabled: isWorking,
      },
      {
        show: hasInstructions,
        text: t('innerLayout.billsPage.allServices.continueButton'),
        onClickHandler: handlePayNow,
        disabled: isWorking,
        loading: isWorking,
      },
      {
        show: !partialStep && !hasInstructions,
        text: t('innerLayout.billsPage.allServices.payNowButton'),
        onClickHandler: handlePayNow,
        disabled: isWorking || isLoading,
        loading: isWorking,
      },
      {
        show: payPartial,
        text: t('innerLayout.billsPage.allServices.payPartiallyButton'),
        onClickHandler: handlePayPartially,
        variant: 'link' as const,
        disabled: isLoading || isWorking,
      },
      {
        show: !payPartial && !partialStep,
        color: 'secondary' as const,
        variant: 'outlined' as const,
        text: t('innerLayout.billsPage.allServices.cancelButton'),
        onClickHandler: handleClose,
        disabled: isWorking,
      },
    ];

    return buttons.filter(b => b.show).map(({ show, ...btn }) => btn);
  };

  const renderContent = () => {
    if (result) {
      if (result.instructions) {
        return (
          <>
            {result.instructions.map((stepItem: any, index: number) => (
              <MultiSectionCard
                key={index}
                title={stepItem.title}
                subtitle={stepItem.description}
                icon={stepItem.icon}
              />
            ))}
          </>
        );
      }

      return (
        <StatusIndicator
          status={result.status === 'success' ? 'success' : 'error'}
          icon={result.status === 'success' ? Check : Xmark}
          text={result.title}
          hintText={result.description}
        />
      );
    }

    if (step === STEP.PARTIAL) return renderPartialPayment();
    return (
      <>
        {renderBalance()}
        {renderDetailsStep()}
      </>
    );
  };

  if (!showInquiryModal) return null;

  return (
    <Modal
      open={showInquiryModal}
      hasCloseIcon
      closeOnClickOutside={false}
      onCloseHandler={handleClose}
      title={result?.status === 'sms' ? result?.title : result ? undefined : renderHeader() as any}
      btnsProps={getBtnsProps()}
      position='center'
    >
      <div className={styles['inquiry-saved-bills__body']}>
        {renderContent()}
      </div>
    </Modal>
  );
};
